import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getEmployeeName } from "../../Config/Employees";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import {
  formatHrmsDate,
  PersonCell,
  StatusBadge,
} from "../../components/Hrms/hrmsUi";
import { hrmsDateBounds, validateCorrection } from "./hrmsDateRules";

interface CorrectionRequest {
  _id: string;
  email: string;
  name?: string;
  date: string;
  requestedLoginTime?: string;
  requestedLogoutTime?: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: string;
  reviewNote?: string;
}

export const AttendanceCorrectionRequests = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const userName = getEmployeeName(userEmail);
  const toast = useToast();
  const { canApproveAttendanceCorrections } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [teamRequests, setTeamRequests] = useState<CorrectionRequest[]>([]);
  const [myRequests, setMyRequests] = useState<CorrectionRequest[]>([]);
  // Approvers (Manager/Admin/HR) review their team's requests but raise their
  // own like anyone else — those go to their own manager.
  const [view, setView] = useState<"team" | "mine">(
    canApproveAttendanceCorrections ? "team" : "mine",
  );
  const isTeamView = view === "team";
  const [selectedRequest, setSelectedRequest] =
    useState<CorrectionRequest | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const bounds = hrmsDateBounds();

  const [form, setForm] = useState({
    date: "",
    requestedLoginTime: "",
    requestedLogoutTime: "",
    reason: "",
  });

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const [mine, team] = await Promise.all([
        EmployeeDataService.getAttendanceCorrections(
          token,
          userEmail,
          "",
          "",
          true,
        ),
        canApproveAttendanceCorrections
          ? EmployeeDataService.getAttendanceCorrections(token, userEmail)
          : null,
      ]);
      if (mine.data.status === "success") {
        setMyRequests(mine.data.data || []);
      }
      if (team?.data.status === "success") {
        setTeamRequests(team.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching correction requests:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (
      !form.date ||
      !form.requestedLoginTime ||
      !form.requestedLogoutTime ||
      !form.reason
    ) {
      toast({
        position: "top",
        title: "Missing details",
        description: "Date, login time, logout time and reason are required.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const loginIso = new Date(`${form.date}T${form.requestedLoginTime}`).toISOString();
    const logoutIso = new Date(`${form.date}T${form.requestedLogoutTime}`).toISOString();
    const dateError = validateCorrection(form.date, loginIso, logoutIso);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSaving(true);
    try {
      const res = await EmployeeDataService.createAttendanceCorrection(token, {
        email: userEmail,
        name: userName,
        date: form.date,
        requestedLoginTime: loginIso,
        requestedLogoutTime: logoutIso,
        reason: form.reason,
      });

      if (res.data.status === "success") {
        toast({
          position: "top",
          title: res.data.message || "Request submitted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        setForm({
          date: "",
          requestedLoginTime: "",
          requestedLogoutTime: "",
          reason: "",
        });
        setView("mine");
        fetchRequests();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to submit request.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const openReview = (request: CorrectionRequest) => {
    setSelectedRequest(request);
    setReviewNote("");
    onOpen();
  };

  const handleReview = async (status: "approved" | "rejected") => {
    if (!selectedRequest) return;
    setIsSaving(true);
    try {
      const res = await EmployeeDataService.reviewAttendanceCorrection(
        token,
        selectedRequest._id,
        { status, reviewNote },
        userEmail,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: `Request ${status}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchRequests();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to review request.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (isoString?: string) => {
    if (!isoString) return "-";
    return DateTime.fromISO(isoString).toFormat("hh:mm a");
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const requests = isTeamView ? teamRequests : myRequests;
  const statusCounts = requests.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  // Pending first, then newest (the API already returns newest first).
  const visibleRequests = requests
    .filter((r) => statusFilter === "all" || r.status === statusFilter)
    .sort((a, b) => Number(b.status === "pending") - Number(a.status === "pending"));

  const filterChips = [
    { key: "all", label: "All", count: requests.length },
    { key: "pending", label: "Pending", count: statusCounts.pending || 0 },
    { key: "approved", label: "Approved", count: statusCounts.approved || 0 },
    { key: "rejected", label: "Rejected", count: statusCounts.rejected || 0 },
  ];

  return (
    <HrmsPageShell
      title="Attendance Corrections"
      description={
        canApproveAttendanceCorrections
          ? "Review your team's requests and raise your own"
          : "Ask for a wrong login/logout time to be corrected"
      }
      sectionKey="attendance"
    >
      <>
        <HrmsCard mb={4} title="Request a correction">
          <Flex direction={{ base: "column", md: "row" }} gap={4} wrap="wrap">
            <FormControl w={{ base: "100%", md: "180px" }} isRequired>
              <FormLabel fontSize="sm">Date</FormLabel>
              <Input
                type="date"
                value={form.date}
                min={bounds.correction.min}
                max={bounds.correction.max}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </FormControl>
            <FormControl w={{ base: "100%", md: "180px" }} isRequired>
              <FormLabel fontSize="sm">Correct Login Time</FormLabel>
              <Input
                type="time"
                value={form.requestedLoginTime}
                onChange={(e) =>
                  setForm({ ...form, requestedLoginTime: e.target.value })
                }
              />
            </FormControl>
            <FormControl w={{ base: "100%", md: "180px" }} isRequired>
              <FormLabel fontSize="sm">Correct Logout Time</FormLabel>
              <Input
                type="time"
                value={form.requestedLogoutTime}
                onChange={(e) =>
                  setForm({ ...form, requestedLogoutTime: e.target.value })
                }
              />
            </FormControl>
            <FormControl flex={1} minW="220px" isRequired>
              <FormLabel fontSize="sm">Reason</FormLabel>
              <Input
                value={form.reason}
                onChange={(e) =>
                  setForm({ ...form, reason: e.target.value })
                }
                placeholder="Why is a correction needed?"
              />
            </FormControl>
            <Flex align="flex-end">
              <Button
                bg="#327171"
                color="white"
                _hover={{ bg: "#285e5e" }}
                onClick={handleSubmitRequest}
                isLoading={isSaving}
              >
                Submit Request
              </Button>
            </Flex>
          </Flex>
        </HrmsCard>

        <Flex gap={2} mb={3} wrap="wrap" align="center">
          {canApproveAttendanceCorrections ? (
            <Flex
              mr={2}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
            >
              {(
                [
                  { key: "team", label: "Team requests" },
                  { key: "mine", label: "My requests" },
                ] as const
              ).map((tab) => (
                <Button
                  key={tab.key}
                  size="sm"
                  borderRadius={0}
                  bg={view === tab.key ? "#327171" : "white"}
                  color={view === tab.key ? "white" : "gray.700"}
                  _hover={{ bg: view === tab.key ? "#285e5e" : "gray.50" }}
                  onClick={() => {
                    setView(tab.key);
                    setStatusFilter("all");
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Flex>
          ) : (
            <Text fontSize="14px" fontWeight="700" color="#495057" m={0} mr={2}>
              My requests
            </Text>
          )}
          {filterChips.map((chip) => {
            const active = statusFilter === chip.key;
            return (
              <Button
                key={chip.key}
                size="sm"
                borderRadius="full"
                variant={active ? "solid" : "outline"}
                bg={active ? "#327171" : "white"}
                color={active ? "white" : "gray.700"}
                borderColor={active ? "#327171" : "gray.200"}
                _hover={{ borderColor: "#327171" }}
                onClick={() => setStatusFilter(chip.key)}
              >
                {chip.label}
                <Text as="span" ml={1.5} opacity={0.75} fontWeight="500">
                  {chip.count}
                </Text>
              </Button>
            );
          })}
        </Flex>

        {isLoading ? (
          <Flex justify="center" align="center" h="40vh">
            <Spinner size="xl" color="#308888" />
          </Flex>
        ) : (
          <HrmsTable
            head={[
              ...(isTeamView ? ["Employee"] : []),
              "Date",
              "Requested Time",
              "Reason",
              "Status",
              ...(isTeamView ? [""] : []),
            ]}
            isEmpty={visibleRequests.length === 0}
            emptyText={
              statusFilter === "all"
                ? "No correction requests yet"
                : `No ${statusFilter} requests`
            }
          >
            {visibleRequests.map((request) => (
                    <tr key={request._id}>
                      {isTeamView && (
                        <td>
                          <PersonCell
                            name={request.name || request.email}
                            subtitle={request.email}
                          />
                        </td>
                      )}
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatHrmsDate(request.date)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatTime(request.requestedLoginTime)}
                        <Text as="span" color="gray.400" mx={1.5}>
                          →
                        </Text>
                        {formatTime(request.requestedLogoutTime)}
                      </td>
                      <td>
                        <Text fontSize="13px" m={0} noOfLines={2} maxW="320px">
                          {request.reason}
                        </Text>
                      </td>
                      <td>
                        <StatusBadge status={request.status} />
                        {request.reviewNote && (
                          <Text fontSize="11px" color="gray.500" m={0} mt={1} noOfLines={1} maxW="200px">
                            {request.reviewNote}
                          </Text>
                        )}
                      </td>
                      {isTeamView && (
                        <td style={{ textAlign: "right" }}>
                          {request.status === "pending" && (
                            <Button
                              size="sm"
                              bg="#327171"
                              color="white"
                              _hover={{ bg: "#285e5e" }}
                              onClick={() => openReview(request)}
                            >
                              Review
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
            ))}
          </HrmsTable>
        )}
      </>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Review Correction — {selectedRequest?.name || selectedRequest?.email}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Review Note</FormLabel>
              <Textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Optional note"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              mr={3}
              onClick={() => handleReview("rejected")}
              isLoading={isSaving}
            >
              Reject
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={() => handleReview("approved")}
              isLoading={isSaving}
            >
              Approve
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
